// LD-5 — LADDER page.
//
// Wired to useLadderCycle + useLadderHistory + useLadderBudget hooks.
// Rewired to the Football Products design kit (FpKit) — Kalshi/Polymarket-clean,
// inline FpKit-token styling, no legacy `.ld-*` global CSS. 5-rung grid,
// entertainment budget banner, build cycle stages, report card, history grid.

import { useMemo } from "react";
import {
  FpHead,
  FpScoreboard,
  FpDisclaimer,
  FpSeam,
  fmtNum,
} from "../football/_fpkit.tsx";
import { Icon } from "../../components/icons/Icon.tsx";
import { useLadderCycle } from "../../ladder/useLadderCycle.ts";
import { useLadderHistory } from "../../ladder/useLadderHistory.ts";
import { useLadderBudget } from "../../ladder/useLadderBudget.ts";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import type { LdCycle, LdRung } from "../../ladder/useLadderCycle.ts";
import type { LdHistoryCycle } from "../../ladder/useLadderHistory.ts";
import type { LdBudget } from "../../ladder/useLadderBudget.ts";

// ─── Demo data (shown when the DB has no cycle yet) ──────

const DEMO_CYCLE: LdCycle = {
  id: "demo-cycle-1",
  cycle_type: "weekend",
  status: "published",
  fixture_window_start: (() => { const d = new Date(); d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7)); return d.toISOString(); })(),
  fixture_window_end: (() => { const d = new Date(); d.setDate(d.getDate() + ((7 - d.getDay() + 7) % 7)); return d.toISOString(); })(),
  pool_size: 26,
  pool_avg_confidence: 0.72,
  pool_avg_clv: 3.8,
  pool_diversity_score: 0.84,
  rungs_published_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
};

const DEMO_RUNGS: readonly LdRung[] = [
  { id: "demo-r1", rung_code: "R1", rung_label: "STARTER", target_odds_min: 1.20, target_odds_max: 1.55, actual_odds: 4.2, selection_count: 3, expected_hit_rate: 0.78, status: "pending", selections_won: 0, selections_lost: 0, selections_void: 0, selections_pending: 3 },
  { id: "demo-r2", rung_code: "R2", rung_label: "MID", target_odds_min: 1.30, target_odds_max: 1.65, actual_odds: 7.8, selection_count: 4, expected_hit_rate: 0.64, status: "pending", selections_won: 0, selections_lost: 0, selections_void: 0, selections_pending: 4 },
  { id: "demo-r3", rung_code: "R3", rung_label: "HIGH", target_odds_min: 1.42, target_odds_max: 1.85, actual_odds: 18.4, selection_count: 5, expected_hit_rate: 0.47, status: "pending", selections_won: 0, selections_lost: 0, selections_void: 0, selections_pending: 5 },
  { id: "demo-r4", rung_code: "R4", rung_label: "ELITE", target_odds_min: 1.55, target_odds_max: 2.05, actual_odds: 42.5, selection_count: 6, expected_hit_rate: 0.31, status: "pending", selections_won: 0, selections_lost: 0, selections_void: 0, selections_pending: 6 },
  { id: "demo-r5", rung_code: "R5", rung_label: "MAX", target_odds_min: 1.65, target_odds_max: 2.10, actual_odds: 124, selection_count: 8, expected_hit_rate: 0.18, status: "pending", selections_won: 0, selections_lost: 0, selections_void: 0, selections_pending: 8 },
];

const DEMO_BUDGET: LdBudget = {
  id: "demo-budget-1",
  monthly_cap: 500,
  currency: "GHC",
  current_period_start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
  current_period_spent: 185,
  amount_remaining: 315,
  is_paused: false,
};

function demoWeekDate(weeksAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - weeksAgo * 7);
  // Set to Friday
  d.setDate(d.getDate() - ((d.getDay() + 2) % 7));
  return d.toISOString();
}

const DEMO_REPORT_CYCLE: LdHistoryCycle = {
  id: "demo-hist-0",
  cycle_type: "weekend",
  status: "completed",
  fixture_window_start: demoWeekDate(1),
  pool_size: 24,
  pool_avg_clv: 3.5,
  created_at: demoWeekDate(1),
  rungs: [
    { rung_code: "R1", rung_label: "STARTER", actual_odds: 4.2, status: "won", selections_won: 3, selections_lost: 0 },
    { rung_code: "R2", rung_label: "MID", actual_odds: 7.8, status: "won", selections_won: 4, selections_lost: 0 },
    { rung_code: "R3", rung_label: "HIGH", actual_odds: 18.4, status: "lost", selections_won: 4, selections_lost: 1 },
    { rung_code: "R4", rung_label: "ELITE", actual_odds: 42.5, status: "lost", selections_won: 5, selections_lost: 1 },
    { rung_code: "R5", rung_label: "MAX", actual_odds: 124, status: "lost", selections_won: 6, selections_lost: 2 },
  ],
};

const DEMO_HISTORY: readonly LdHistoryCycle[] = [
  DEMO_REPORT_CYCLE,
  {
    id: "demo-hist-1", cycle_type: "weekend", status: "completed", fixture_window_start: demoWeekDate(2), pool_size: 22, pool_avg_clv: 4.1, created_at: demoWeekDate(2),
    rungs: [
      { rung_code: "R1", rung_label: "STARTER", actual_odds: 4.2, status: "won", selections_won: 3, selections_lost: 0 },
      { rung_code: "R2", rung_label: "MID", actual_odds: 7.8, status: "won", selections_won: 4, selections_lost: 0 },
      { rung_code: "R3", rung_label: "HIGH", actual_odds: 18.4, status: "won", selections_won: 5, selections_lost: 0 },
      { rung_code: "R4", rung_label: "ELITE", actual_odds: 42.5, status: "won", selections_won: 6, selections_lost: 0 },
      { rung_code: "R5", rung_label: "MAX", actual_odds: 124, status: "lost", selections_won: 6, selections_lost: 2 },
    ],
  },
  {
    id: "demo-hist-2", cycle_type: "weekend", status: "completed", fixture_window_start: demoWeekDate(3), pool_size: 25, pool_avg_clv: 3.9, created_at: demoWeekDate(3),
    rungs: [
      { rung_code: "R1", rung_label: "STARTER", actual_odds: 4.2, status: "won", selections_won: 3, selections_lost: 0 },
      { rung_code: "R2", rung_label: "MID", actual_odds: 7.8, status: "won", selections_won: 4, selections_lost: 0 },
      { rung_code: "R3", rung_label: "HIGH", actual_odds: 18.4, status: "lost", selections_won: 4, selections_lost: 1 },
      { rung_code: "R4", rung_label: "ELITE", actual_odds: 42.5, status: "won", selections_won: 6, selections_lost: 0 },
      { rung_code: "R5", rung_label: "MAX", actual_odds: 124, status: "lost", selections_won: 6, selections_lost: 2 },
    ],
  },
  {
    id: "demo-hist-3", cycle_type: "weekend", status: "completed", fixture_window_start: demoWeekDate(4), pool_size: 20, pool_avg_clv: 2.8, created_at: demoWeekDate(4),
    rungs: [
      { rung_code: "R1", rung_label: "STARTER", actual_odds: 4.2, status: "won", selections_won: 3, selections_lost: 0 },
      { rung_code: "R2", rung_label: "MID", actual_odds: 7.8, status: "lost", selections_won: 3, selections_lost: 1 },
      { rung_code: "R3", rung_label: "HIGH", actual_odds: 18.4, status: "lost", selections_won: 3, selections_lost: 2 },
      { rung_code: "R4", rung_label: "ELITE", actual_odds: 42.5, status: "lost", selections_won: 4, selections_lost: 2 },
      { rung_code: "R5", rung_label: "MAX", actual_odds: 124, status: "lost", selections_won: 5, selections_lost: 3 },
    ],
  },
  {
    id: "demo-hist-4", cycle_type: "weekend", status: "completed", fixture_window_start: demoWeekDate(5), pool_size: 23, pool_avg_clv: 3.6, created_at: demoWeekDate(5),
    rungs: [
      { rung_code: "R1", rung_label: "STARTER", actual_odds: 4.2, status: "won", selections_won: 3, selections_lost: 0 },
      { rung_code: "R2", rung_label: "MID", actual_odds: 7.8, status: "won", selections_won: 4, selections_lost: 0 },
      { rung_code: "R3", rung_label: "HIGH", actual_odds: 18.4, status: "won", selections_won: 5, selections_lost: 0 },
      { rung_code: "R4", rung_label: "ELITE", actual_odds: 42.5, status: "lost", selections_won: 4, selections_lost: 2 },
      { rung_code: "R5", rung_label: "MAX", actual_odds: 124, status: "lost", selections_won: 5, selections_lost: 3 },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  return fmtNum(n);
}

function fmtDec(n: number | null | undefined, d = 2): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(d) : "—";
}

function riskLabel(code: string): string {
  const map: Record<string, string> = {
    R1: "Low", R2: "Medium", R3: "Elevated", R4: "High", R5: "Extreme",
  };
  return map[code] ?? "—";
}

function stakeForRung(code: string): number {
  const map: Record<string, number> = { R1: 5, R2: 10, R3: 20, R4: 50, R5: 100 };
  return map[code] ?? 0;
}

function targetReturn(code: string, odds: number): number {
  return Math.round(stakeForRung(code) * odds);
}

function cycleStageState(status: string): { stages: { lab: string; name: string; desc: string; state: string }[] } {
  const STAGES = [
    { lab: "Mon–Wed", name: "Pool building", desc: "18 agents → candidates → ranked by CLV", state: "" },
    { lab: "Thu", name: "Rung allocation", desc: "Layered by odds: R1 base → R5 amplifier", state: "" },
    { lab: "Fri 12:00", name: "Slips published", desc: "5 ladders released to dashboard", state: "" },
    { lab: "Sun 23:00", name: "Settlement + Report", desc: "Auto-grade, stats, Monday briefing", state: "" },
  ];

  if (status === "completed" || status === "failed") {
    return { stages: STAGES.map((s) => ({ ...s, state: "done" })) };
  }
  if (status === "published" || status === "live") {
    return {
      stages: [
        { ...STAGES[0]!, state: "done" },
        { ...STAGES[1]!, state: "done" },
        { ...STAGES[2]!, state: "live" },
        { ...STAGES[3]!, state: "" },
      ],
    };
  }
  if (status === "locked" || status === "optimising") {
    return {
      stages: [
        { ...STAGES[0]!, state: "done" },
        { ...STAGES[1]!, state: "live" },
        { ...STAGES[2]!, state: "" },
        { ...STAGES[3]!, state: "" },
      ],
    };
  }
  // scanning, eliminating, initialising
  return {
    stages: [
      { ...STAGES[0]!, state: "live" },
      { ...STAGES[1]!, state: "" },
      { ...STAGES[2]!, state: "" },
      { ...STAGES[3]!, state: "" },
    ],
  };
}

function liveStageIdx(status: string): number {
  if (status === "completed" || status === "failed") return 4;
  if (status === "published" || status === "live") return 3;
  if (status === "locked" || status === "optimising") return 2;
  return 1;
}

// ─── Shared inline-style atoms ────────────────────────────

const MONO_LABEL: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

const CARD_STYLE: React.CSSProperties = {
  background: "var(--bg-elev)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  marginBottom: 16,
};

const SECTION_TITLE: React.CSSProperties = {
  fontFamily: "'Bebas Neue', sans-serif",
  fontSize: 20,
  lineHeight: 1,
  margin: 0,
  color: "var(--text)",
  letterSpacing: "0.01em",
};

function ghostButton(extra?: React.CSSProperties): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    border: "1px solid var(--border)",
    background: "var(--bg-elev)",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    cursor: "pointer",
    ...extra,
  };
}

// ─── RungCard ─────────────────────────────────────────────

function RungCard({ rung }: { rung: LdRung }): JSX.Element {
  const stake = stakeForRung(rung.rung_code);
  const target = targetReturn(rung.rung_code, rung.actual_odds);
  const hitPct = rung.expected_hit_rate > 0
    ? (rung.expected_hit_rate * 100).toFixed(0)
    : "—";
  const settled = rung.status === "won" || rung.status === "lost";
  const accentBorder = !settled && (rung.status === "live");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-elev)",
        border: `1px solid ${accentBorder ? "var(--fp-accent)" : "var(--border)"}`,
        borderRadius: 12,
        padding: 16,
      }}
    >
      {/* Head: rung number + tier */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 8,
            border: `1px solid ${accentBorder ? "var(--fp-accent)" : "var(--border)"}`,
            background: "var(--bg-inset)",
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 22,
            lineHeight: 1,
            color: accentBorder ? "var(--fp-accent-text)" : "var(--text)",
          }}
        >
          {rung.rung_code.replace("R", "")}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...MONO_LABEL, color: accentBorder ? "var(--fp-accent-text)" : "var(--text-tertiary)" }}>
            {rung.rung_label}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", lineHeight: 1.3 }}>
            {rung.rung_label} Ladder
          </div>
        </div>
      </div>

      {/* Stake line */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          fontFamily: "var(--font-mono)",
        }}
      >
        <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>GH₵</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>{stake}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fp-accent-text)" }}>
          {fmtDec(rung.actual_odds, 1)}×
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
        Target return <span style={{ color: "var(--text-tertiary)" }}>→</span>{" "}
        <span style={{ fontWeight: 600, color: "var(--text)" }}>GH₵{fmt(target)}</span>
      </div>

      {/* Meta grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
          margin: "14px 0",
          paddingTop: 14,
          borderTop: "1px solid var(--border-faint)",
        }}
      >
        {[
          { k: "Legs", v: String(rung.selection_count) },
          { k: "Hit-rate", v: `${hitPct}%` },
          { k: "Combined", v: `${fmtDec(rung.actual_odds)}×` },
          { k: "Risk", v: riskLabel(rung.rung_code) },
        ].map((m) => (
          <div key={m.k}>
            <div style={MONO_LABEL}>{m.k}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
              {m.v}
            </div>
          </div>
        ))}
      </div>

      {/* Status indicator for settled rungs */}
      {rung.status === "won" && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <span
            style={{
              color: "var(--fp-won)",
              fontWeight: 600,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Icon name="check" size={14} /> All {rung.selection_count} legs hit
          </span>
        </div>
      )}
      {rung.status === "lost" && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <span style={{ color: "var(--fp-lost)", fontWeight: 500, fontSize: 13 }}>
            {rung.selections_won}W / {rung.selections_lost}L
            {rung.selections_void > 0 ? ` / ${rung.selections_void}V` : ""}
          </span>
        </div>
      )}
      {(rung.status === "pending" || rung.status === "live") && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>
            {rung.status === "live"
              ? `${rung.selections_won}W / ${rung.selections_lost}L / ${rung.selections_pending ?? 0} pending`
              : `${rung.selection_count} legs awaiting kickoff`}
          </span>
        </div>
      )}

      {/* Foot */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: "auto",
          paddingTop: 12,
          borderTop: "1px solid var(--border-faint)",
        }}
      >
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Expected · <b style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{hitPct}% hit rate</b>
        </div>
        <button type="button" style={ghostButton({ padding: "6px 14px" })}>Place</button>
      </div>
    </div>
  );
}

// ─── ReportCard (from last completed cycle) ─────────────

function ReportCard({ cycle }: { cycle: LdHistoryCycle }): JSX.Element {
  const dateStr = new Date(cycle.fixture_window_start).toLocaleDateString("en-GB", {
    month: "short", day: "numeric",
  });

  return (
    <div style={CARD_STYLE}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          padding: "14px 16px",
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <span style={SECTION_TITLE}>Monday Report Card</span>
        <span style={{ ...MONO_LABEL, fontWeight: 500 }}>
          · last cycle {cycle.cycle_type} · {dateStr}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 10,
          padding: 16,
        }}
      >
        {cycle.rungs.map((r) => {
          const stake = stakeForRung(r.rung_code);
          const won = r.status === "won";
          const returnAmt = won ? Math.round(stake * r.actual_odds) : 0;
          const totalLegs = r.selections_won + r.selections_lost;

          return (
            <div
              key={r.rung_code}
              style={{
                background: "var(--bg-inset)",
                border: "1px solid var(--border-faint)",
                borderRadius: 8,
                padding: 12,
                textAlign: "center",
              }}
            >
              <div style={MONO_LABEL}>{r.rung_label}</div>
              <div
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 22,
                  lineHeight: 1,
                  margin: "6px 0",
                  color: won ? "var(--fp-won)" : "var(--fp-lost)",
                }}
              >
                {won ? `+GH₵${fmt(returnAmt)}` : `−GH₵${stake}`}
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 3, margin: "8px 0" }}>
                {Array.from({ length: totalLegs }).map((_, idx) => (
                  <span
                    key={idx}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: idx >= r.selections_won ? "var(--fp-lost)" : "var(--fp-won)",
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>
                {r.selections_won}/{totalLegs} legs · {won ? "Clean sweep" : "Busted"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── History row ────────────────────────────────────────

function HistoryRow({ cycle, idx }: { cycle: LdHistoryCycle; idx: number }): JSX.Element {
  const TIER_ORDER = ["R1", "R2", "R3", "R4", "R5"];
  const dateStr = new Date(cycle.fixture_window_start).toLocaleDateString("en-GB", {
    month: "short", day: "numeric",
  });

  const cells = TIER_ORDER.map((code) => {
    const rung = cycle.rungs.find((r) => r.rung_code === code);
    if (!rung) return { type: "neg", val: "—", label: code.replace("R", "") };
    const stake = stakeForRung(code);
    const won = rung.status === "won";
    const amt = won ? Math.round(stake * rung.actual_odds) : stake;
    return {
      type: won ? "pos" : "neg",
      val: won ? `+${fmt(amt)}` : `-${stake}`,
      label: rung.rung_label,
    };
  });

  const net = cells.reduce((sum, c) => {
    const v = parseInt(c.val.replace(/[+,−\s]/g, "").replace("-", ""), 10) || 0;
    return sum + (c.type === "pos" ? v : -v);
  }, 0);
  const netColor = net >= 0 ? "var(--fp-won)" : "var(--fp-lost)";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto repeat(5, 1fr) auto",
        gap: 8,
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid var(--border-faint)",
      }}
    >
      <div style={{ ...MONO_LABEL, minWidth: 52, paddingTop: 0 }}>
        C{idx + 1}
        <div style={{ color: "var(--text-quaternary)", letterSpacing: "0.02em", marginTop: 2 }}>{dateStr}</div>
      </div>
      {cells.map((c, i) => (
        <div
          key={i}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: c.type === "pos" ? "var(--fp-won)" : "var(--fp-lost)",
          }}
        >
          <span style={{ ...MONO_LABEL, display: "block", fontSize: 9, marginBottom: 2 }}>{c.label}</span>
          GH₵{c.val}
        </div>
      ))}
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 700,
          textAlign: "right",
          minWidth: 70,
          color: netColor,
        }}
      >
        {net >= 0 ? "+" : "−"}GH₵{fmt(Math.abs(net))}
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────

export function LadderPage(): JSX.Element {
  const { cycle, rungs, loading, error } = useLadderCycle();
  const { cycles, loading: histLoading } = useLadderHistory();
  const { budget } = useLadderBudget();

  const rungsForStake = rungs.length > 0 ? rungs : DEMO_RUNGS;
  const totalStake = useMemo(
    () => rungsForStake.reduce((s, r) => s + stakeForRung(r.rung_code), 0),
    [rungsForStake],
  );

  const lastCompleted = useMemo(
    () => cycles.find((c) => c.status === "completed") ?? null,
    [cycles],
  );

  const effectiveStatus = cycle?.status ?? DEMO_CYCLE.status;

  const stageInfo = useMemo(
    () => cycleStageState(effectiveStatus),
    [effectiveStatus],
  );

  const stageIdx = liveStageIdx(effectiveStatus);

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!cycle });

  // Loading state
  if (loading) {
    return (
      <AwaitingDataPanel eyebrow="LADDER" title="Loading…" body="Authenticated read pending." />
    );
  }

  // Error state
  if (error) {
    return (
      <AwaitingDataPanel eyebrow="LADDER" title="Pipeline unavailable." body={error} />
    );
  }

  // Demo fallbacks — show full UI even when the DB has no cycle yet
  const isDemo = !cycle;
  const activeCycle = cycle ?? DEMO_CYCLE;
  const activeRungs = rungs.length > 0 ? rungs : DEMO_RUNGS;
  const activeBudget = budget ?? DEMO_BUDGET;
  const activeCycles = cycles.length > 0 ? cycles : DEMO_HISTORY;
  const activeLastCompleted = lastCompleted ?? DEMO_REPORT_CYCLE;

  // Date formatting
  const windowStart = new Date(activeCycle.fixture_window_start);
  const windowEnd = new Date(activeCycle.fixture_window_end);
  const dateRange = `${windowStart.toLocaleDateString("en-GB", { month: "short", day: "numeric" })}–${windowEnd.toLocaleDateString("en-GB", { day: "numeric" })}`;

  const budgetSpent = activeBudget.current_period_spent;
  const budgetCap = activeBudget.monthly_cap;
  const budgetPct = budgetCap > 0 ? Math.round((budgetSpent / budgetCap) * 100) : 0;

  const statusLabel = activeCycle.status === "published" ? "published" : activeCycle.status;

  return (
    <div className="fp" data-screen-label="06 LADDER">
      <FpHead
        kicker="LADDER"
        title="Weekly ladders"
        badge="WEEKLY"
        status={`${activeCycle.cycle_type} · ${statusLabel}`}
        live={activeCycle.status === "live"}
      />

      {/* ── Doctrine fallback when no live cycle ── */}
      {isDemo && (
        <DoctrineSection
          probabilityPicks={doctrineProbPicks}
          valuePicks={doctrineValuePicks}
          isFullFallback
          substrateEmpty={substrateEmpty}
          substrateWeek={substrateWeek}
        />
      )}

      {/* ── Demo showcase banner ── */}
      {isDemo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            marginBottom: 12,
            borderRadius: 8,
            background: "var(--bg-inset)",
            border: "1px solid var(--border-faint)",
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "2px 7px",
              borderRadius: 4,
              background: "var(--fp-accent-soft)",
              color: "var(--fp-accent-text)",
              border: "1px solid var(--border-faint)",
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            SHOWCASE
          </span>
          Sample data — live rungs appear once the pipeline publishes a cycle.
        </div>
      )}

      {/* ── Sub-line + actions ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
          5 rungs · low-to-extreme · cycle{" "}
          <span style={{ fontWeight: 600, color: "var(--text)" }}>
            {activeCycle.cycle_type} · {statusLabel}
          </span>{" "}
          · settles <span style={{ fontWeight: 600, color: "var(--text)" }}>Sun 23:00</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={ghostButton()}>
            <Icon name="info" size={14} /> How LADDER works
          </button>
          <button
            type="button"
            style={ghostButton({
              color: "var(--text)",
              borderColor: "var(--fp-accent)",
            })}
          >
            Place all rungs · GH₵{totalStake}{" "}
            <span style={{ color: "var(--fp-accent-text)" }}>→</span>
          </button>
        </div>
      </div>

      {/* ── Entertainment budget banner ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          padding: 16,
          ...CARD_STYLE,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 8,
            border: "1px solid var(--border)",
            background: "var(--bg-inset)",
            color: "var(--fp-accent-text)",
          }}
        >
          <Icon name="info" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>
              LADDER is your entertainment budget
            </span>
            <span style={MONO_LABEL}>SEPARATE BANKROLL</span>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--text-secondary)" }}>
            These slips are designed for the{" "}
            <b style={{ color: "var(--text)" }}>thrill of a big multiplier</b>, not
            consistent ROI. Stake what you'd spend on a Friday night out — never your
            ROLLOVER or Sika capital. Strikza ringfences this bankroll automatically.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
          <div style={{ minWidth: 180 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 5 }}>
              <span style={MONO_LABEL}>This month</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, color: "var(--text)" }}>
                GH₵{fmt(budgetSpent)} / GH₵{fmt(budgetCap)}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: "var(--bg-inset)", border: "1px solid var(--border-faint)", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(100, budgetPct)}%`,
                  background: "var(--fp-accent)",
                  borderRadius: 999,
                }}
              />
            </div>
          </div>
          <button type="button" style={ghostButton({ whiteSpace: "nowrap" })}>
            Adjust budget
          </button>
        </div>
      </div>

      {/* ── Cycle: 5-rung grid ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <h2 style={SECTION_TITLE}>This week's ladders</h2>
          <span style={{ ...MONO_LABEL, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--text-quaternary)", display: "inline-block" }} />
            {dateRange}
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--text-quaternary)", display: "inline-block" }} />
            {activeCycle.status === "published" || activeCycle.status === "live"
              ? "All slips locked"
              : activeCycle.status}
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--text-quaternary)", display: "inline-block" }} />
            {activeCycle.pool_size != null && `${activeCycle.pool_size} in pool`}
          </span>
          {activeCycle.status === "live" && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                ...MONO_LABEL,
                color: "var(--fp-accent-text)",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--fp-accent)", display: "inline-block" }} />
              Settles Sun 23:00
            </span>
          )}
        </div>

        {activeRungs.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 12,
            }}
          >
            {activeRungs.map((r) => (
              <RungCard key={r.rung_code} rung={r} />
            ))}
          </div>
        ) : (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
            Rungs are being assembled. Check back when the cycle reaches "published" status.
          </div>
        )}
      </div>

      <FpSeam />

      {/* ── Build cycle stages ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
          <h3 style={{ ...SECTION_TITLE, fontSize: 18 }}>Build cycle</h3>
          <span style={{ ...MONO_LABEL, fontWeight: 500 }}>
            Stage {stageIdx} of 4
            {activeCycle.pool_size != null && ` · ${activeCycle.pool_size} candidates in pool`}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10,
          }}
        >
          {stageInfo.stages.map((s, i) => {
            const isDone = s.state === "done";
            const isLive = s.state === "live";
            return (
              <div
                key={i}
                style={{
                  background: "var(--bg-elev)",
                  border: `1px solid ${isLive ? "var(--fp-accent)" : "var(--border)"}`,
                  borderRadius: 10,
                  padding: 14,
                  opacity: !isDone && !isLive ? 0.6 : 1,
                }}
              >
                <div
                  style={{
                    ...MONO_LABEL,
                    color: isLive ? "var(--fp-accent-text)" : isDone ? "var(--text-secondary)" : "var(--text-tertiary)",
                  }}
                >
                  {s.lab}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", margin: "5px 0 4px" }}>
                  {s.name}
                </div>
                <div style={{ fontSize: 11.5, lineHeight: 1.45, color: "var(--text-tertiary)" }}>
                  {s.desc}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Pool stats (if available) ── */}
      {activeCycle.pool_avg_clv != null && (
        <FpScoreboard
          tiles={[
            { label: "Pool Size", value: String(activeCycle.pool_size ?? "—") },
            { label: "Avg CLV", value: `${fmtDec(activeCycle.pool_avg_clv ?? 0, 1)}%` },
            { label: "Avg Confidence", value: `${((activeCycle.pool_avg_confidence ?? 0) * 100).toFixed(0)}%` },
            { label: "Diversity Score", value: fmtDec(activeCycle.pool_diversity_score ?? 0, 2) },
          ]}
        />
      )}

      {/* ── Last cycle report card ── */}
      {activeLastCompleted.rungs.length > 0 && (
        <ReportCard cycle={activeLastCompleted} />
      )}

      {/* ── Cycle history ── */}
      {!histLoading && activeCycles.length > 0 && (
        <div style={CARD_STYLE}>
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid var(--border-faint)",
            }}
          >
            <span style={SECTION_TITLE}>Cycle history</span>
          </div>
          <div style={{ padding: "4px 16px 12px" }}>
            {activeCycles.slice(0, 12).map((c, i) => (
              <HistoryRow key={c.id} cycle={c} idx={i} />
            ))}
          </div>
        </div>
      )}

      {/* ── Disclosure ── */}
      <FpDisclaimer text="Manual placement only — STRIKZA shows prepared rungs through the curated boundary. Never fictional past performance, never fictional in-flight rungs. LADDER uses a separate entertainment bankroll, not your ROLLOVER or Sika capital." />
    </div>
  );
}
